import every from 'lodash/every';
import find from 'lodash/find';
import isMatch from 'lodash/isMatch';
import some from 'lodash/some';

import type {ModelChangeMessageType, relatedFieldType} from './types';

type ArrayRelatedFieldType = Array<relatedFieldType>;

/* be careful! functional programming experiment below */

/*
execute funcToRun when all conditions are true using message as predicated

this function is used on all runWhen* functions below
and ca be used directly on components handleModelChange methods
when the component should call a update function based on two different
conditions
*/
export const runWhenAll = (
  message: ModelChangeMessageType,
  conditions: Array<(message: ModelChangeMessageType) => boolean>,
  funcToRun: (message: ModelChangeMessageType) => any
): unknown =>
  every(conditions.map((condition) => condition(message))) &&
  funcToRun(message);

/* same as above, but will run funcToRun if ANY of conditions are true */
export const runWhenAny = (
  message: ModelChangeMessageType,
  conditions: Array<(message: ModelChangeMessageType) => boolean>,
  funcToRun: (message: ModelChangeMessageType) => any
): unknown =>
  some(conditions.map((condition) => condition(message))) && funcToRun(message);

/* related objs checkers */
const allRelatedMatchMessage = (
  modelsQuery: string | Array<string>,
  query: relatedFieldType | ArrayRelatedFieldType,
  message: ModelChangeMessageType
): boolean =>
  anyModelMatchMessage(modelsQuery, message) &&
  every(
    (Array.isArray(query) ? query : [query]).map(
      // @ts-expect-error the 'related' attr doesn't exist but its ok
      (query) => find(message.related, query) != null
    )
  );

export const allRelatedMatch =
  (
    modelsQuery: string | Array<string>,
    query: relatedFieldType | ArrayRelatedFieldType
  ): ((message: ModelChangeMessageType) => boolean) =>
  (message: ModelChangeMessageType) =>
    allRelatedMatchMessage(modelsQuery, query, message);

export const anyRelatedMatch =
  (query: relatedFieldType | ArrayRelatedFieldType) =>
  (message: ModelChangeMessageType): boolean =>
    every(
      (Array.isArray(query) ? query : [query]).map(
        // @ts-expect-error the 'related' attr doesn't exist but its ok
        (query) => find(message.related, query) != null
      )
    );

export const runWhenAllRelatedMatch = (
  message: ModelChangeMessageType,
  modelsQuery: string | Array<string>,
  related: relatedFieldType | ArrayRelatedFieldType,
  funcToRun: (message: ModelChangeMessageType) => unknown
): unknown =>
  runWhenAll(message, [allRelatedMatch(modelsQuery, related)], funcToRun);

/* model checkers */
const anyModelMatchMessage = (
  modelsQuery: string | Array<string>,
  message: ModelChangeMessageType
): boolean =>
  some(
    (Array.isArray(modelsQuery) ? modelsQuery : [modelsQuery]).map(
      (query) => query === message.model
    )
  );

export const anyModelMatch =
  (
    modelsQuery: string | Array<string>
  ): ((msg: ModelChangeMessageType) => boolean) =>
  (message: ModelChangeMessageType) =>
    anyModelMatchMessage(modelsQuery, message);

export const runWhenAnyModelMatch = (
  message: ModelChangeMessageType,
  modelsQuery: string | Array<string>,
  funcToRun: (message: ModelChangeMessageType) => any
): unknown => runWhenAll(message, [anyModelMatch(modelsQuery)], funcToRun);

/* other field checkers */
const allFieldsMatchMessage = (
  modelsQuery: string | Array<string>,
  fieldsQuery: Record<string, any>,
  message: ModelChangeMessageType
): boolean =>
  anyModelMatchMessage(modelsQuery, message) && isMatch(message, fieldsQuery);

export const allFieldsMatch =
  (modelsQuery: string | Array<string>, fieldsQuery: Record<string, any>) =>
  (message: ModelChangeMessageType): boolean =>
    allFieldsMatchMessage(modelsQuery, fieldsQuery, message);

export const runWhenAllFieldsMatch = (
  message: ModelChangeMessageType,
  modelsQuery: string | Array<string>,
  fields: Record<string, any>,
  funcToRun: (message: ModelChangeMessageType) => any
): unknown =>
  runWhenAll(message, [allFieldsMatch(modelsQuery, fields)], funcToRun);

/* model id checkers */
const modelIdMatchMessage = (
  modelsQuery: string | Array<string>,
  id: string | number,
  message: ModelChangeMessageType
): boolean =>
  allFieldsMatchMessage(
    modelsQuery,
    {
      id,
    },
    message
  );

export const modelIdMatch =
  (modelsQuery: string | Array<string>, id: string | number) =>
  (message: ModelChangeMessageType): boolean =>
    modelIdMatchMessage(modelsQuery, id, message);

export const runWhenModelIdMatch = (
  message: ModelChangeMessageType,
  modelsQuery: string | Array<string>,
  id: string | number,
  funcToRun: (message: ModelChangeMessageType) => any
): unknown => runWhenAll(message, [modelIdMatch(modelsQuery, id)], funcToRun);
